/**
 * Smoothly scrolls to a referenced element with customizable behavior
 *
 * @param ref - Reference to the target HTML element
 * @param baseDuration - Duration of the scroll animation in milliseconds
 * @param offset - Vertical offset from the target element in pixels
 * @param onScrollComplete - Callback function to execute when scrolling completes
 */
export const smartScrollToRef = (ref, baseDuration = 400, offset = -100, onScrollComplete) => {
    if (!(ref === null || ref === void 0 ? void 0 : ref.current))
        return;
    const getOffset = () => ref.current.getBoundingClientRect().top + window.scrollY + offset;
    const startY = window.scrollY;
    const targetY = getOffset();
    const distance = targetY - startY;
    const duration = baseDuration;
    const startTime = performance.now();
    function animateScroll(currentTime) {
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(timeElapsed / duration, 1);
        const ease = progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress;
        window.scrollTo(0, startY + distance * ease);
        if (progress < 1) {
            requestAnimationFrame(animateScroll);
        }
        else {
            correctScroll();
        }
    }
    /**
     * Ensures the scroll position is stable before completing
     * Handles cases where dynamic content might shift the target position
     */
    function correctScroll() {
        let lastPos = null;
        let stableCount = 0;
        const MAX_STABLE_COUNT = 3;
        const MAX_CORRECTION_TIME = 3000;
        const start = Date.now();
        function step() {
            const currentOffset = getOffset();
            if (lastPos !== null && Math.abs(currentOffset - lastPos) < 1) {
                stableCount++;
            }
            else {
                stableCount = 0;
            }
            lastPos = currentOffset;
            window.scrollTo(0, currentOffset);
            if (stableCount >= MAX_STABLE_COUNT ||
                Date.now() - start > MAX_CORRECTION_TIME) {
                if (onScrollComplete) {
                    onScrollComplete();
                }
                return;
            }
            requestAnimationFrame(step);
        }
        step();
    }
    requestAnimationFrame(animateScroll);
};
